import type { ConfigApp, Pedido, RotaPayload, StatusBackend } from './types';

const BASE = '/api';

async function jsonReq<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  const body = await r.json().catch(() => ({}));
  if (r.status === 401) {
    // sessão caiu (expirou / logout em outra aba) — devolve para a tela de senha
    window.dispatchEvent(new CustomEvent('auth:expirou'));
  }
  if (!r.ok) throw new Error(body?.erro || `HTTP ${r.status}`);
  return body as T;
}

export async function listarPedidos(
  data: string = 'all',
  signal?: AbortSignal,
): Promise<Pedido[]> {
  const q = new URLSearchParams({ data });
  const r = await jsonReq<{ ok: boolean; pedidos: Pedido[] }>(`/pedidos?${q}`, { signal });
  return r.pedidos;
}

// Pedidos a partir de uma data (YYYY-MM-DD). Usado pelo dashboard para limitar a
// janela e não puxar o histórico inteiro.
export async function listarPedidosDesde(desde: string, signal?: AbortSignal): Promise<Pedido[]> {
  const r = await jsonReq<{ ok: boolean; pedidos: Pedido[] }>(
    `/pedidos?desde=${encodeURIComponent(desde)}`,
    { signal },
  );
  return r.pedidos;
}

export async function buscarPedido(id: number): Promise<Pedido> {
  const r = await jsonReq<{ ok: boolean; pedido: Pedido }>(`/pedidos/${id}`);
  return r.pedido;
}

export async function patchStatus(id: number, status: StatusBackend): Promise<Pedido> {
  const r = await jsonReq<{ ok: boolean; pedido: Pedido }>(`/pedidos/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  return r.pedido;
}

export interface AtualizarPedidoInput {
  nome_cliente?: string;
  telefone?: string | null;
  endereco_entrega?: string;
  pagamento?: string | null;
  total?: string | null;
  mapa_link?: string | null;
  observacoes?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  regeocodificar?: boolean;
}

export async function atualizarPedido(id: number, input: AtualizarPedidoInput): Promise<Pedido> {
  const r = await jsonReq<{ ok: boolean; pedido: Pedido }>(`/pedidos/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return r.pedido;
}

export async function deletarPedido(id: number): Promise<{ id: number; numero_ticket: string }> {
  const r = await jsonReq<{ ok: boolean; id: number; numero_ticket: string }>(
    `/pedidos/${id}`,
    { method: 'DELETE' },
  );
  return { id: r.id, numero_ticket: r.numero_ticket };
}

export interface NovoPedidoInput {
  nome_cliente: string;
  endereco_entrega: string;
  telefone?: string | null;
  total?: string | null;
  pagamento?: string | null;
  observacoes?: string | null;
  mapa_link?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  itens?: Array<{ descricao: string; qtd?: number }>;
}

export async function criarPedido(input: NovoPedidoInput): Promise<Pedido> {
  const r = await jsonReq<{ ok: boolean; pedido: Pedido }>(`/pedidos`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return r.pedido;
}

export async function buscarRota(data?: string): Promise<RotaPayload | null> {
  const q = data ? `?data=${encodeURIComponent(data)}` : '';
  const r = await jsonReq<{ ok: boolean; rota: RotaPayload | null }>(`/rotas${q}`);
  return r.rota;
}

export interface GerarRotaResposta {
  rota: RotaPayload | null;
  aviso?: string;
  diagnostico?: {
    total_do_dia: number;
    total_separado: number;
    sem_geocoding: number;
    regeocodificados: number;
    enderecos_falha: Array<{ numero_ticket: string; endereco_entrega: string }>;
  };
}

export async function gerarRota(data?: string): Promise<GerarRotaResposta> {
  const r = await jsonReq<{
    ok: boolean;
    rota: RotaPayload | null;
    aviso?: string;
    diagnostico?: GerarRotaResposta['diagnostico'];
  }>(`/rotas/gerar`, {
    method: 'POST',
    body: JSON.stringify(data ? { data } : {}),
  });
  return { rota: r.rota, aviso: r.aviso, diagnostico: r.diagnostico };
}

export async function buscarConfig(): Promise<ConfigApp> {
  const r = await jsonReq<{ ok: boolean } & ConfigApp>(`/config`);
  return r;
}
